import { useEffect, useState } from "react";
import { Button, Form, Input, Select, Row, Col, Typography } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TOptionType, TResponseModel } from "../../../types/config";
import { eOVERALLISTSTATUS, eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  AddEditL2Evaluation,
  GetSpecificLevel2EvalDetails,
} from "../../../utils/api.constant";
import { CheckCircleFilled, CheckOutlined, CloseCircleFilled, CloseOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import EvalLevel1View from "../evaluation/l1-view";

// Type for DrawerProps, which are the properties passed to the EvalL2Form component
type DrawerProps = {
  serialNumber: string;
  level1EvalId: number; // ID from Level 1 evaluation
  level2EvalId: number;
  isOpen?: any;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
  overallId: number;
};

// Type for the form fields
export type TEvalL2Form = {
  currentStageId: number | null; // Action/Stage chosen in L2 eval
  description: string; // Additional description from user
};

// Main component for the Level 2 Evaluation Form
export default function EvalL2Form(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();

  useEffect(() => {
    if (props?.level2EvalId) {
      fetchSpecificLevel2Details();
    }
  }, []);

  // To fetch specific config param
  const fetchSpecificLevel2Details = async () => {
    try {
      const payload = {
        data: {
          id: props.level2EvalId,
        },
      };
      setIsLoading(true);
      const response: TResponseModel = await post(
        GetSpecificLevel2EvalDetails,
        payload
      );
      if (response) {
        const { data, dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          form.setFieldsValue({ ...data });
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseCircleFilled />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // List of available actions in the L2 process
  const actionOptions: TOptionType[] = [
    { label: "Move to teardown", value: eOVERALLISTSTATUS.TEAR_DOWN },
    { label: "No fault found", value: eOVERALLISTSTATUS.NO_FAULT },
  ];

  // Default values for the form on initial render/reset
  const defaultValues: TEvalL2Form = {
    currentStageId: null,
    description: "",
  };

  /**
   * Handles the form submission logic.
   * Sends data to AddEditL2Evaluation API and provides user feedback.
   */
  const onFinish = async (values: TEvalL2Form) => {
    try {
      const payload = {
        data: {
          id: props?.level2EvalId || 0,
          serialNumber: props.serialNumber,
          level1EvalId: props.level1EvalId,
          customerUploadDetailId: props.overallId,
          currentStageId: values.currentStageId,
          description: values.description,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditL2Evaluation, payload);

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ display: "flex" }}>
      <div style={{ width: "75vw" }}>
        <EvalLevel1View id={props?.overallId} />
      </div>
      <div style={{ width: "25vw" }}>
        <FormLayout>
          <Form
            form={form}
            name="register"
            onFinish={onFinish}
            initialValues={defaultValues}
            layout="vertical"
            autoComplete="off"
          >
            {/* Form header area, includes title and close button */}
            <FormHeader>
              <Row
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Col>
                  <h3 style={{ color: "var(--bs-dark)", marginTop: "10px" }}>
                    Eval - L2 Process
                  </h3>
                </Col>
                <Col>
                  <Button
                    type="default"
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={() => {
                      props.onCloseDrawer();
                    }}
                  >
                    Close
                  </Button>
                </Col>
              </Row>
            </FormHeader>

            {/* Main form content/body */}
            <FormContent>
              <Row gutter={16} className="formRowHeight">
                <Col span={24}>
                  {/* Text input for optional description */}
                  <Form.Item name="serialNo" label="Serial Number">
                    <Input
                      defaultValue={props?.serialNumber}
                      readOnly
                      size="large"
                      placeholder="Serial Number"
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  {/* Dropdown to select the action for current L2 evaluation */}
                  <Form.Item
                    name="currentStageId"
                    label="Action"
                    rules={[
                      {
                        required: true,
                        message: "Please select an action",
                      },
                    ]}
                  >
                    <Select
                      size="large"
                      placeholder="Select action"
                      options={actionOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  {/* Text input for optional description */}
                  <Form.Item name="description" label="Description">
                    <TextArea size="large" placeholder="Description" />
                  </Form.Item>
                </Col>
              </Row>
            </FormContent>

            {/* Footer area with submit (Save) button */}
            <FormFooter className="formFooter">
              <FormRow className="formRow">
                <Button
                  className="btnDimensions"
                  type="primary"
                  onClick={form.submit}
                  disabled={isLoading}
                >
                  Save
                </Button>
              </FormRow>
            </FormFooter>
          </Form>
        </FormLayout>
      </div>
    </div>
  );
}
